import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Chip, CircularProgress, Alert, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  IconButton, Divider, Tooltip, Grid
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { vortxColors } from '@vortx/shared-ui';
import { API_BASE_URL } from '../../services/api';

/**
 * WholesaleMarketsTab — Admin panel for managing VPP wholesale market profiles.
 * Displays one card per ISO zone with energy, capacity, and ancillary pricing.
 * Super-admin only (access gated by parent TariffManagementPage).
 */
const WholesaleMarketsTab = ({ currentUser }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [success, setSuccess] = useState(null);

  // Edit dialog state
  const [editProfile, setEditProfile] = useState(null);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);

  const getAuthHeaders = useCallback(async () => {
    const token = await currentUser.getIdToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, [currentUser]);

  const fetchProfiles = useCallback(async () => {
    if (!currentUser) return;
    try {
      setLoading(true);
      setError(null);
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/vpp/profiles`, { headers });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Failed to fetch profiles: ${response.status}`);
      }
      const data = await response.json();
      setProfiles(data.profiles || []);
    } catch (err) {
      console.error('Failed to fetch VPP profiles:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser, getAuthHeaders]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/vpp/refresh`, {
        method: 'POST',
        headers
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Refresh failed: ${response.status}`);
      }
      setSuccess('Market data refresh initiated');
      // Re-fetch after a short delay to allow refresh to process
      setTimeout(() => fetchProfiles(), 2000);
    } catch (err) {
      console.error('Failed to refresh market data:', err);
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  const handleEditOpen = (profile) => {
    setEditProfile(profile);
    setEditData({
      capacity_prices: profile.capacity_prices ? { ...profile.capacity_prices } : {
        capacity_price_kw_year: '',
        auction_name: '',
        delivery_year: ''
      },
      dr_programs: profile.dr_programs ? profile.dr_programs.map(p => ({ ...p })) : []
    });
  };

  const handleEditClose = () => {
    setEditProfile(null);
    setEditData(null);
  };

  const handleSave = async () => {
    if (!editProfile || !editData) return;
    try {
      setSaving(true);
      setError(null);
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/vpp/profiles/${editProfile.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(editData)
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Save failed: ${response.status}`);
      }
      setSuccess('Profile updated successfully');
      handleEditClose();
      fetchProfiles();
    } catch (err) {
      console.error('Failed to save profile:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // DR program helpers
  const handleAddDrProgram = () => {
    setEditData(prev => ({
      ...prev,
      dr_programs: [...prev.dr_programs, {
        program_name: '',
        utility: '',
        compensation_type: 'capacity',
        capacity_rate_kw_year: '',
        event_rate_kwh: '',
        events_per_year: '',
        eligible_months: [],
        min_commitment_kw: '',
      }]
    }));
  };

  const handleRemoveDrProgram = (index) => {
    setEditData(prev => ({
      ...prev,
      dr_programs: prev.dr_programs.filter((_, i) => i !== index)
    }));
  };

  const handleDrProgramChange = (index, field, value) => {
    setEditData(prev => ({
      ...prev,
      dr_programs: prev.dr_programs.map((p, i) => i === index ? { ...p, [field]: value } : p)
    }));
  };

  const handleCapacityChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      capacity_prices: { ...prev.capacity_prices, [field]: value }
    }));
  };

  // Determine refresh status for a profile
  const getRefreshStatus = (profile) => {
    const status = profile.refresh_status || profile.status;
    if (status === 'fresh' || status === 'success') {
      return { label: 'Fresh', color: vortxColors.success };
    }
    if (status === 'stale' || status === 'pending') {
      return { label: 'Stale', color: vortxColors.warning };
    }
    return { label: 'Failed', color: vortxColors.error };
  };

  // Get most recent refresh timestamp across all profiles
  const lastRefreshAt = profiles.reduce((latest, p) => {
    const ts = p.last_refresh_at;
    if (!ts) return latest;
    return !latest || ts > latest ? ts : latest;
  }, null);

  const formatTimestamp = (ts) => {
    if (!ts) return 'Never';
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return ts;
    }
  };

  const formatCurrency = (val) => {
    if (val === null || val === undefined || val === '') return '—';
    const num = Number(val);
    if (isNaN(num)) return '—';
    return `$${num.toFixed(2)}`;
  };

  // Energy/AS prices stored as $/kWh — convert to $/MWh for display
  const formatLMP = (val_kwh) => {
    if (val_kwh === null || val_kwh === undefined || val_kwh === '') return '—';
    const num = Number(val_kwh);
    if (isNaN(num)) return '—';
    return `$${(num * 1000).toFixed(2)}`;
  };

  const formatPercent = (val) => {
    if (val === null || val === undefined || val === '') return '—';
    const num = Number(val);
    if (isNaN(num)) return '—';
    return `${(num * 100).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: vortxColors.gold }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Top Bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ color: vortxColors.white }}>
            Wholesale Market Profiles
          </Typography>
          {lastRefreshAt && (
            <Typography variant="body2" sx={{ color: vortxColors.stone }}>
              Last refresh: {formatTimestamp(lastRefreshAt)}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          disabled={refreshing}
          onClick={handleRefresh}
          sx={{
            bgcolor: vortxColors.gold,
            color: '#000',
            '&:hover': { bgcolor: alpha(vortxColors.gold, 0.8) },
            '&.Mui-disabled': { bgcolor: alpha(vortxColors.gold, 0.4) }
          }}
        >
          {refreshing ? 'Refreshing...' : 'Refresh Now'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {profiles.length === 0 && !error && (
        <Alert severity="info">
          No wholesale market profiles found. Profiles are created automatically when market data is seeded.
        </Alert>
      )}

      {/* Profile Cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {profiles.map((profile) => {
          const refreshStatus = getRefreshStatus(profile);
          const energy = profile.energy_prices || {};
          const capacity = profile.capacity_prices || {};
          const ancillary = profile.ancillary_services || {};
          const drPrograms = profile.dr_programs || [];

          return (
            <Paper
              key={profile.id}
              sx={{
                p: 3,
                bgcolor: vortxColors.charcoal,
                border: `1px solid ${vortxColors.teal}`,
                borderRadius: 2
              }}
            >
              {/* Header Row */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Chip
                    label={`${profile.iso} \u2014 ${profile.region || 'Zone'}`}
                    size="small"
                    sx={{
                      bgcolor: alpha(vortxColors.gold, 0.15),
                      color: vortxColors.gold,
                      fontWeight: 700,
                      fontSize: '0.85rem'
                    }}
                  />
                  <Typography variant="subtitle1" sx={{ color: vortxColors.white, fontWeight: 600 }}>
                    {profile.display_name || profile.id}
                  </Typography>
                  <Chip
                    label={refreshStatus.label}
                    size="small"
                    sx={{
                      bgcolor: alpha(refreshStatus.color, 0.15),
                      color: refreshStatus.color,
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                  />
                </Box>
                <Tooltip title="Edit manual fields">
                  <IconButton
                    size="small"
                    onClick={() => handleEditOpen(profile)}
                    sx={{ color: vortxColors.stone, '&:hover': { color: vortxColors.gold } }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Data Sections */}
              <Grid container spacing={3}>
                {/* Energy Prices */}
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" sx={{ color: vortxColors.stone, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Energy Prices
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <DataRow label="Avg DA LMP" value={formatLMP(energy.avg_da_lmp_kwh)} unit="/MWh" />
                    <DataRow label="Peak" value={formatLMP(energy.peak_da_lmp_kwh)} unit="/MWh" />
                    <DataRow label="Off-Peak" value={formatLMP(energy.offpeak_da_lmp_kwh)} unit="/MWh" />
                    <DataRow label="Negative Hours" value={energy.negative_price_hours != null ? `${energy.negative_price_hours}` : '—'} />
                    <DataRow label="Volatility" value={formatPercent(energy.price_volatility_index)} />
                    {energy.source_period && (
                      <Typography variant="caption" sx={{ color: vortxColors.stone, mt: 0.5 }}>
                        Source: {energy.source_period}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                {/* Capacity Prices */}
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" sx={{ color: vortxColors.stone, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Capacity Prices
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <DataRow label="Price" value={formatCurrency(capacity.capacity_price_kw_year)} unit="/kW-yr" />
                    <DataRow label="Auction" value={capacity.auction_name || '—'} />
                    <DataRow label="Delivery Year" value={capacity.delivery_year || '—'} />
                  </Box>
                </Grid>

                {/* Ancillary Services + DR */}
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" sx={{ color: vortxColors.stone, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Ancillary Services
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <DataRow label="Reg Up" value={formatLMP(ancillary.reg_up_avg_kwh)} unit="/MWh" />
                    <DataRow label="Reg Down" value={formatLMP(ancillary.reg_down_avg_kwh)} unit="/MWh" />
                    <DataRow label="Spinning Reserve" value={formatLMP(ancillary.spin_reserve_avg_kwh)} unit="/MWh" />
                  </Box>
                  {drPrograms.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Chip
                        label={`${drPrograms.length} DR Program${drPrograms.length !== 1 ? 's' : ''}`}
                        size="small"
                        sx={{
                          bgcolor: alpha(vortxColors.teal, 0.2),
                          color: vortxColors.white,
                          fontSize: '0.75rem'
                        }}
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Paper>
          );
        })}
      </Box>

      {/* Edit Dialog */}
      <Dialog
        open={!!editProfile}
        onClose={handleEditClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: vortxColors.charcoal,
            color: vortxColors.white,
            border: `1px solid ${vortxColors.teal}`
          }
        }}
      >
        {editProfile && editData && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">
                Edit — {editProfile.display_name || editProfile.id}
              </Typography>
              <IconButton onClick={handleEditClose} size="small" sx={{ color: vortxColors.stone }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ borderColor: vortxColors.teal }}>
              {/* Auto-refreshed fields (read-only) */}
              <Typography variant="caption" sx={{ color: vortxColors.stone, fontWeight: 600, textTransform: 'uppercase' }}>
                Auto-Refreshed Fields
              </Typography>
              <Typography variant="caption" sx={{ color: vortxColors.stone, display: 'block', mb: 1 }}>
                source: gridstatus — updated automatically
              </Typography>
              <Box sx={{ mb: 2, p: 1.5, bgcolor: alpha(vortxColors.green, 0.3), borderRadius: 1 }}>
                <DataRow label="Avg DA LMP" value={formatLMP(editProfile.energy_prices?.avg_da_lmp_kwh)} unit="/MWh" />
                <DataRow label="Peak" value={formatLMP(editProfile.energy_prices?.peak_da_lmp_kwh)} unit="/MWh" />
                <DataRow label="Off-Peak" value={formatLMP(editProfile.energy_prices?.offpeak_da_lmp_kwh)} unit="/MWh" />
                <DataRow label="Reg Up" value={formatLMP(editProfile.ancillary_services?.reg_up_avg_kwh)} unit="/MWh" />
                <DataRow label="Reg Down" value={formatLMP(editProfile.ancillary_services?.reg_down_avg_kwh)} unit="/MWh" />
              </Box>

              <Divider sx={{ borderColor: vortxColors.teal, my: 2 }} />

              {/* Capacity Prices (editable) */}
              <Typography variant="caption" sx={{ color: vortxColors.stone, fontWeight: 600, textTransform: 'uppercase' }}>
                Capacity Prices (Manual)
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                <TextField
                  label="Price ($/kW-yr)"
                  size="small"
                  type="number"
                  value={editData.capacity_prices.capacity_price_kw_year}
                  onChange={(e) => handleCapacityChange('capacity_price_kw_year', e.target.value)}
                  fullWidth
                  sx={textFieldSx}
                />
                <TextField
                  label="Auction Name"
                  size="small"
                  value={editData.capacity_prices.auction_name}
                  onChange={(e) => handleCapacityChange('auction_name', e.target.value)}
                  fullWidth
                  sx={textFieldSx}
                />
                <TextField
                  label="Delivery Year"
                  size="small"
                  value={editData.capacity_prices.delivery_year}
                  onChange={(e) => handleCapacityChange('delivery_year', e.target.value)}
                  fullWidth
                  sx={textFieldSx}
                />
              </Box>

              <Divider sx={{ borderColor: vortxColors.teal, my: 2 }} />

              {/* DR Programs (editable) */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" sx={{ color: vortxColors.stone, fontWeight: 600, textTransform: 'uppercase' }}>
                  DR Programs (Manual)
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddDrProgram}
                  sx={{ color: vortxColors.gold, textTransform: 'none' }}
                >
                  Add Program
                </Button>
              </Box>

              {editData.dr_programs.length === 0 && (
                <Typography variant="body2" sx={{ color: vortxColors.stone, fontStyle: 'italic' }}>
                  No DR programs configured
                </Typography>
              )}

              {editData.dr_programs.map((program, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    border: `1px solid ${alpha(vortxColors.teal, 0.5)}`,
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                      Program {idx + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveDrProgram(idx)}
                      sx={{ color: vortxColors.error }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <TextField
                    label="Program Name"
                    size="small"
                    value={program.program_name}
                    onChange={(e) => handleDrProgramChange(idx, 'program_name', e.target.value)}
                    fullWidth
                    sx={textFieldSx}
                  />
                  <TextField
                    label="Utility"
                    size="small"
                    value={program.utility}
                    onChange={(e) => handleDrProgramChange(idx, 'utility', e.target.value)}
                    fullWidth
                    sx={textFieldSx}
                  />
                  <TextField
                    label="Compensation Type"
                    size="small"
                    value={program.compensation_type}
                    onChange={(e) => handleDrProgramChange(idx, 'compensation_type', e.target.value)}
                    placeholder="capacity / energy / both"
                    fullWidth
                    sx={textFieldSx}
                  />
                  <TextField
                    label="Capacity Rate ($/kW-yr)"
                    size="small"
                    type="number"
                    value={program.capacity_rate_kw_year ?? ''}
                    onChange={(e) => handleDrProgramChange(idx, 'capacity_rate_kw_year', e.target.value)}
                    fullWidth
                    sx={textFieldSx}
                  />
                  <TextField
                    label="Event Rate ($/kWh)"
                    size="small"
                    type="number"
                    value={program.event_rate_kwh ?? ''}
                    onChange={(e) => handleDrProgramChange(idx, 'event_rate_kwh', e.target.value)}
                    fullWidth
                    sx={textFieldSx}
                  />
                  <TextField
                    label="Min Commitment (kW)"
                    size="small"
                    type="number"
                    value={program.min_commitment_kw ?? ''}
                    onChange={(e) => handleDrProgramChange(idx, 'min_commitment_kw', e.target.value)}
                    fullWidth
                    sx={textFieldSx}
                  />
                </Box>
              ))}
            </DialogContent>
            <DialogActions sx={{ borderTop: `1px solid ${vortxColors.teal}`, px: 3, py: 1.5 }}>
              <Button onClick={handleEditClose} sx={{ color: vortxColors.stone }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{
                  bgcolor: vortxColors.gold,
                  color: '#000',
                  '&:hover': { bgcolor: alpha(vortxColors.gold, 0.8) }
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

// Shared text field styling for dark theme
const textFieldSx = {
  '& .MuiInputBase-root': { color: vortxColors.white },
  '& .MuiInputLabel-root': { color: vortxColors.stone },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: vortxColors.teal },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: vortxColors.gold
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: vortxColors.gold
  }
};

// Simple label-value row used in both cards and edit dialog
const DataRow = ({ label, value, unit }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
    <Typography variant="body2" sx={{ color: vortxColors.stone, fontSize: '0.8rem' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: vortxColors.white, fontWeight: 500, fontSize: '0.8rem' }}>
      {value}{unit && value !== '—' ? <Typography component="span" sx={{ color: vortxColors.stone, fontSize: '0.7rem', ml: 0.3 }}>{unit}</Typography> : null}
    </Typography>
  </Box>
);

export default WholesaleMarketsTab;
